import type {
  PathwayScenario,
  PathwayScenarioLight,
  PathwayScenarioOutput,
  PathwayScenarioWithInventory,
  NewPathwayScenario,
  PathwayScenarioParameters,
  LocalPathwayScenario,
} from '@/types';
import { api } from './api';

const scenarioApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPathwayScenario: builder.query<PathwayScenario, number>({
      query: (id) => `pathway-scenarios/${id}`,
    }),
    getPathwayScenarios: builder.query<PathwayScenarioLight[], number>({
      query: (inventoryId) => `inventories/${inventoryId}/pathway-scenarios`,
    }),
    getAllPathwayScenarios: builder.query<PathwayScenarioWithInventory[], void>(
      {
        query: () => `pathway-scenarios`,
      },
    ),
    simulation: builder.query<
      PathwayScenarioOutput,
      PathwayScenarioParameters & { scenarioId: number }
    >({
      query: ({ scenarioId, ...parameters }) => ({
        url: `pathway-scenarios/${scenarioId}/simulation`,
        method: 'POST',
        body: parameters,
      }),
    }),
    downloadPathwayScenario: builder.mutation<
      {},
      PathwayScenarioParameters & { scenarioId: number }
    >({
      query: ({ scenarioId, ...parameters }) => ({
        url: `pathway-scenarios/${scenarioId}/download`,
        method: 'POST',
        body: parameters,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        queryFulfilled.then(({ data }: any) => {
          window.location = data.url;
        });
      },
    }),
    newPathwayScenario: builder.mutation<PathwayScenario, NewPathwayScenario>({
      query: ({ inventoryId, name }) => ({
        url: `inventories/${inventoryId}/pathway-scenarios`,
        method: 'POST',
        body: {
          name,
        },
      }),
    }),
    deletePathwayScenario: builder.mutation<
      void,
      { scenarioId: number; inventoryId: number }
    >({
      query: ({ scenarioId }) => ({
        url: `pathway-scenarios/${scenarioId}`,
        method: 'DELETE',
      }),
      onQueryStarted: async ({ scenarioId, inventoryId }, { dispatch }) => {
        dispatch(
          scenarioApi.util.updateQueryData(
            'getPathwayScenarios',
            inventoryId,
            (current) =>
              current.filter((v: PathwayScenarioLight) => v.id != scenarioId),
          ),
        );
      },
    }),
    savePathwayScenario: builder.mutation<
      PathwayScenario,
      LocalPathwayScenario
    >({
      query: (scenario) => ({
        url: `pathway-scenarios/${scenario.id}`,
        method: 'POST',
        body: scenario,
      }),
      onQueryStarted: async (scenario, { dispatch }) => {
        dispatch(
          scenarioApi.util.updateQueryData(
            'getPathwayScenario',
            scenario.id,
            // this is awkward, not all interventions will have id,
            // need to fix types...
            () => scenario as PathwayScenario,
          ),
        );
      },
    }),
  }),
});

export const {
  useGetPathwayScenarioQuery,
  useLazyGetPathwayScenarioQuery,
  useSimulationQuery,
  useDownloadPathwayScenarioMutation,
  useGetPathwayScenariosQuery,
  useGetAllPathwayScenariosQuery,
  useNewPathwayScenarioMutation,
  useSavePathwayScenarioMutation,
  useDeletePathwayScenarioMutation,
} = scenarioApi;
